import { enAdmin } from "./en.admin";
import { enShell } from "./en.shell";
import { enCampaigns } from "./en.campaigns";
import { enCard } from "./en.card";
import { enReports } from "./en.reports";
import { enEditor } from "./en.editor";
import { enOffers } from "./en.offers";
import { enOps } from "./en.ops";
import { enLanders } from "./en.landers";
import { enAdaptive } from "./en.adaptive";
import { enPlan } from "./en.plan";

/**
 * The key set, and the English fallback.
 *
 * English is the *source of truth for the keys*, not the product language:
 * `TKey = keyof typeof en` is what turns a missing Russian string into a
 * compile error instead of a screen that shows `campaign.tab.funnel` to an
 * operator. Add a key here first, then in `ru.ts` — tsc will insist.
 *
 * Scope is the surfaces the product is being rewritten on — the campaign card,
 * the buyer's ad-link dialog and the admin screen behind it. This module is a
 * seam we open there, not a migration of the app.
 */
export const en = {
  ...enAdmin,
  ...enShell,
  ...enCampaigns,
  ...enCard,
  ...enReports,
  ...enEditor,
  ...enOffers,
  ...enOps,
  ...enLanders,
  ...enAdaptive,
  ...enPlan,
  // — header —
  "campaign.back": "Campaigns",
  "campaign.action.pause": "Pause",
  "campaign.action.activate": "Activate",
  "campaign.action.more": "More",
  "campaign.action.duplicate": "Duplicate",
  "campaign.action.archive": "Archive",
  "campaign.notFound": "Campaign not found — it may have been archived.",
  "campaign.toast.paused": "{name} paused",
  "campaign.toast.activated": "{name} activated",
  "campaign.toast.failed": "That did not work",
  "campaign.archive.title": "Archive “{name}”?",
  "campaign.archive.body": "It stops taking traffic and can be restored from the archived filter.",
  "campaign.archive.done": "{name} archived",

  // — tabs —

  // — stats: conversion outcomes —

  // — landers: which page is working and which is quietly costing money —

  // — settings —
  // The field's own label is NOT here: the platform declares it
  // (internal/sourcetype, AskField.Label) and each one differs — Meta says
  // "pixel", Google says "conversion id". Only the generic hint lives here.
  "branch.trigger.back": "Back",
  "branch.trigger.back.hint": "The visitor pressed the browser's Back button.",
  "branch.trigger.exit_intent": "Exit intent",
  "branch.trigger.exit_intent.hint": "The pointer left the window — on a phone, a fast fling back up the page.",
  "branch.trigger.idle": "Idle",
  "branch.trigger.idle.hint": "No taps, scrolls or keys for the number of seconds you set.",
  "branch.trigger.scroll_end": "Scroll to end",
  "branch.trigger.scroll_end.hint": "The visitor reached the bottom of the page.",
  "branch.trigger.next_step": "Next step",
  "branch.trigger.next_step.hint": "The first page's button leads to the second-step landings.",

  // — the ad link: what the buyer fills in, and what he copies out —

  // — список кампаний: экран, который медиабайер открывает каждое утро —

  // — шапки-полосы над колонками —
  "campaigns.band.traffic": "Traffic",
  "campaigns.band.conversions": "Conversions",
  "campaigns.band.money": "Money",



  // — admin: ad-link templates —
  "adlink.title": "Ad link templates",
  "adlink.subtitle":
    "What each platform asks the buyer for, and what he copies back out. Edited in internal/sourcetype — the screen reads the same generated registry the dialog does.",
  "adlink.platform": "Platform",
  "adlink.ask": "What we ask him",
  "adlink.ask.empty": "This source has no ad account, so there is nothing to ask and nothing to give.",
  "adlink.ask.key": "Key",
  "adlink.ask.label": "Label (Russian)",
  "adlink.ask.rule": "Rule",
  "adlink.ask.required": "Required",
  "adlink.ask.error": "Message when it fails",
  "adlink.give": "What he copies",
  "adlink.give.label": "Label",
  "adlink.give.adField": "Field in the ad account",
  "adlink.give.value": "Value",
  "adlink.rules": "Every platform's rule, and the mistake it catches",
  "adlink.rules.example": "Looks like",
  "adlink.rules.mistake": "Pasted instead",
  "adlink.yes": "yes",
  "adlink.no": "no",
  "adlink.source":
    "These strings are data, not UI copy: a platform renames a field or widens an id far more often than this screen changes. Edit internal/sourcetype/specs_*.go, then regenerate: UPDATE_SOURCETYPE_SNAPSHOT=1 go test ./internal/sourcetype/...",
  // — навигация оболочки —
  // The eight rows a buyer works from, plus the admin area behind them. The
  // labels are the whole point of the rewrite: "Reporting → Geo" told the
  // operator which table he was in, never what he could do there.
  "nav.today": "Today",
  "nav.campaigns": "Campaigns",
  "nav.journal": "Journal",
  "nav.domains": "Domains",
  "nav.settings": "Settings",
  "nav.newCampaign": "New campaign",
  "nav.admin": "Admin",
  "nav.search": "Campaign, click, offer, lander",
  "nav.signOut": "Sign out",
  "lib.title": "Library",
  "journal.title": "Journal",
  "journal.tab.clicks": "Clicks",
  "journal.tab.conversions": "Conversions",
  "journal.tab.bots": "Bots",
  "settings.title": "Settings",
  "settings.tab.workspace": "General",
  "settings.tab.goals": "Goals",
  "settings.tab.connections": "Connections",
  "admin.title": "Admin",
  "admin.subtitle": "Platform surfaces: they affect every workspace, not just yours.",
  "admin.tab.flows": "Flows",
  "admin.tab.flowTemplates": "Flow templates",
  "admin.tab.tagManager": "Tag Manager",
  "admin.tab.fraudWeights": "Anti-fraud weights",
  "admin.tab.ipIntel": "IP intel",
  "admin.tab.edge": "Edge",
  "admin.tab.adLinks": "Ad-link templates",

  // — «Сегодня»: the home screen a media buyer opens every morning —
  "today.error": "Could not load today's numbers",

  // — day one: no campaign has ever been created —
  "today.checklist.network": "Affiliate network",
  "today.checklist.offer": "Offer",
  "today.checklist.lander": "Lander",

  // — the money line —

  // — today's campaigns, sorted by profit —

  // — the PnL chart —

  // — unacknowledged rule events —
  // — tracking domains: a media buyer connects a domain he bought himself —
  // One contiguous block at the end of the dictionary on purpose: a parallel
  // branch renames this screen's route, and a conflict here should be a
  // one-hunk resolution, not a merge across the file.
  "domains.action.checking": "Checking…",
  "domains.search.label": "Search domains",
  "domains.col.domain": "Domain",
  "domains.col.state": "State",
  "domains.badge.default": "default",
  "domains.default.make": "Use for new campaigns",
  "domains.default.done": "{fqdn} will now be used for new campaigns",
  "domains.default.failed": "Could not change the default domain",

  "domains.state.working": "Working",
  "domains.state.waiting": "Waiting for DNS",
  "domains.state.failed": "Error",
  "domains.state.disabled": "Switched off",
  "domains.state.unknown": "Not checked yet",
  "domains.state.working.hint": "The domain answers and campaigns can run on it.",
  "domains.state.waiting.hint": "The record has not reached the internet yet. Registrars usually need 5–30 minutes, occasionally a couple of hours.",
  "domains.state.failed.hint": "We could not connect the domain. The reason is below — fix it and press Check.",
  "domains.state.disabled.hint": "You switched this domain off. The setup is kept, but clicks are not served.",
  "domains.state.unknown.hint": "We have not checked this domain yet. Press Check.",

  "domains.blocked.body": "Until the state is “Working”, the Start button on a campaign using this domain returns an error. Nothing else about the campaign is wrong — the address simply does not answer yet.",

  "domains.empty.title": "You have no domain yet",
  "domains.empty.body": "A domain is the address your ad link lives on: the ad sends a person to your address, the tracker records the click, and only then forwards him to the offer. Buy a domain at any registrar — or take a subdomain of one you already own — and connect it here.",
  "domains.empty.action": "Connect a domain",

  "domains.create.title": "Connect a domain",
  "domains.create.description": "The address your ad link will live on.",
  "domains.port.label": "How the domain connects",
  "domains.port.native.summary": "In one click — the DNS record is set for you",
  "domains.port.native.register": "An address «{adapter}» does not have yet is added there by itself.",
  "domains.port.native.done": "{fqdn} connected. It starts working once the record is live — usually within minutes.",
  "domains.port.free.title": "Your free domains in «{adapter}»",
  "domains.port.free.take": "Connect",
  "domains.port.free.none": "No free domains in «{adapter}» — type an address below or buy a new one.",
  "domains.port.other": "Another address",
  "domains.port.buy": "Buy a new one in «{adapter}»",
  "domains.port.manual.records": "What to set in the domain's DNS",
  "domains.port.manual.steps": "Connect the domain — it appears as «Waiting for DNS». Add the record at your DNS host exactly as shown. The tracker checks it every minute; «Check now» on the domain checks at once.",
  "domains.port.manual.limit.title": "When clicks will arrive",
  "domains.port.manual.limit.body": "The record passes the check at any DNS host, but clicks reach the tracker only when the domain's zone routes the tracker edge itself (your own Cloudflare with the tracker Worker). A domain at another DNS host will not take clicks yet — a connected domain source sets everything up for you.",
  "domains.port.failed.noAnswer": "«{adapter}» did not answer",
  "domains.port.failed.notWired": "«{adapter}» is not set up for this tracker yet — we are on it",
  "domains.port.failed.connector": "Your own domain service connects once the connector is available",
  "domains.port.failed.change": "Change in «Connections»",
  "domains.create.fqdn": "Domain address",
  "domains.create.fqdn.placeholder": "go.example.com",
  "domains.create.fqdn.hint": "Better a subdomain than the bare domain: if you own example.com, write go.example.com. The site on example.com keeps working.",
  "domains.create.default": "Use this domain for new campaigns",
  "domains.create.submit": "Connect",
  "domains.create.done": "{fqdn} added. Now add the DNS record below.",
  "domains.create.failed": "Could not add the domain",

  "domains.detail.checkedNever": "Not checked yet",

  "domains.steps.2": "Add the record below exactly as written. Copy the values, do not retype them.",

  "domains.record.none": "This provider needs nothing from you — it writes the record itself.",


  "domains.check.bound": "{fqdn} works — you can run campaigns on it",
  "domains.check.waiting": "Not there yet: {reason}",
  "domains.check.waitingPlain": "The record has not reached the internet yet. Wait ten minutes and press Check again.",
  "domains.check.failed": "Check failed",

  "domains.serve.label": "Serve clicks",
  "domains.serve.hint": "Switched off, the domain keeps its settings but stops taking clicks.",
  "domains.serve.failed": "Could not switch the domain",

  "domains.delete.done": "Domain deleted",
  "domains.delete.failed": "Could not delete the domain",

  "domains.loadFailed": "Could not load your domains",
  "domains.unavailable.title": "Domains cannot be connected right now",
  "domains.unavailable.body": "The domain service is not set up on this installation, so the tracker cannot connect a domain. Write to an administrator — nothing here can be done from your side.",

  // — общая хромка таблиц —
  "table.csv": "Download CSV",
  "table.selectAll": "Select all",
  "table.selectRow": "Select row",
  "table.empty": "No rows",
  "table.prev": "Previous",
  "table.next": "Next",
  "table.perPage": "per page",
  "table.summary": "{from}–{to} of {total}",
  "table.page": "Page {page} of {total}",

  /* ── Library: offers, landers, traffic sources, affiliate networks ──────
   *
   * One contiguous block, appended rather than interleaved, so the screens
   * being translated in parallel branches meet in a merge nobody has to think
   * about. Keys mirror the screen, not the proto.
   */

  "tagPicker.create": "Create tag",
  "lib.common.retry": "Retry",
  "lib.common.failed": "Something went wrong",
  "lib.common.edit": "Edit",
  "lib.common.save": "Save",
  "lib.common.saving": "Saving…",
  "lib.common.create": "Create",
  "lib.common.cancel": "Cancel",
  "lib.common.copy": "Copy",
  "lib.common.copied": "{what} copied",
  "lib.common.archive": "Archive",
  "lib.common.duplicate": "Duplicate",
  "lib.common.refresh": "Refresh",
  "lib.common.loading": "Loading…",
  "lib.common.saveFailed": "Could not save",
  "lib.common.archiveFailed": "Could not archive",
  "lib.common.optional": "(optional)",
  "lib.common.notSet": "Not set.",
  "lib.common.col.name": "Name",
  "lib.common.col.status": "Status",
  "lib.common.col.actions": "Actions",
  "lib.common.total": "{count} in total",

  "lib.status.active": "Live",
  "lib.status.paused": "Paused",
  "lib.status.archived": "Archived",
  "lib.status.draft": "Draft",






  "lib.flowMode.single": "One offer",
  "lib.flowMode.split": "Split test",
  "lib.flowMode.weighted": "Rotation by weight",
  "lib.flowMode.rules": "By rules",
  "lib.flowMode.template": "From a template",

  "lib.landers.archivedOne": "“{name}” archived",




  "lib.sources.title": "Traffic sources",
  "lib.sources.new": "New source",
  "lib.sources.newFirst": "Add your first source",
  "lib.sources.loadFailed": "Could not load the traffic sources",
  "lib.sources.empty.title": "No traffic sources yet",
  "lib.sources.empty.body": "Pick a platform — Facebook, TikTok, Google — and the tracker fills in its tags and its ad link for you.",

  "lib.sources.saveFailed": "Could not save the traffic source",




  // — Statistics (/stats + /stats/geo) —
  // One contiguous block; every key is prefixed `stats.` and `ru.ts` must
  // carry a string for each of them or the build fails.








  // — Т5: shell, period, campaigns list —
  "common.apply": "Apply",
  "common.cancel": "Cancel",
  "common.undo": "Undo",
  "common.undoFailed": "Could not restore",
  "common.deleteFailed": "Could not delete",
  "common.done": "Done",
  "common.soon": "Coming soon",
  "word.campaigns": "campaign|campaigns",
  "word.groups": "group|groups",
  "nav.sections": "Sections",
  "nav.reports": "Reports",
  "nav.health": "Data health",
  "nav.role.admin": "Admin",
  "nav.role.viewer": "Viewer",
  "nav.role.buyer": "Buyer",
  "scope.label": "Whose data",
  "scope.mine": "Mine",
  "scope.all": "All",
  "period.label": "Period",
  "period.today": "Today",
  "period.yesterday": "Yesterday",
  "period.7d": "7 days",
  "period.14d": "14 days",
  "period.30d": "30 days",
  "period.mtd": "This month",
  "period.lastmonth": "Last month",
  "period.custom": "Custom",
  "period.compareWith": "Compare with",
  "period.cmp.prev": "previous period",
  "period.cmp.year": "a year ago",
  "period.cmp.off": "no comparison",
  "period.badge.prev": "vs previous {days}",
  "period.badge.year": "vs a year ago",
  "period.against": "against {range}",
  "period.basis": "Conversions by",
  "period.basis.lead": "lead date",
  "period.basis.conv": "conversion date",
  "period.basis.click": "click date",
  "foot.lastClick": "Last click {age} ago",
  "foot.noClicks": "No clicks for two days",
  "foot.lastPostback": "Postback {age} ago",
  "foot.noPostbacks": "No postbacks for two days",
  "foot.unavailable": "Status unavailable",
  "foot.day": "Day by {tz}",
  "foot.currency": "Money in USD",
  "settings.tab.alerts": "Alerts",
  "table.bulk.selected": "Selected {count}",
  "table.bulk.clear": "Clear selection",
  "table.bulk.region": "Bulk actions",
  "table.bulk.cleared": "Selection cleared",
  "table.bulk.ok": "Done: {ok} of {total}",
  "table.bulk.failed": "Failed: {failed} of {total}. {reason}",
  "kpi.botsCut": "bots {pct}",
  "kpi.cr": "CR {cr}",
  "kpi.approve": "{pct} of decided",
  "kpi.hold": "In hold",
  "kpi.inFlight": "≈ {sum} more on the way",
  "kpi.epc": "EPC {epc}",
  "kpi.cpa": "CPA {cpa}",
  "kpi.was": "was {value}",
  "cols.visits": "Visits",
  "cols.clicks": "Clicks",
  "cols.uniques": "Uniques",
  "cols.bots": "Bots",
  "cols.botsPct": "Bots %",
  "cols.lpShows": "LP shows",
  "cols.lpClicks": "LP clicks",
  "cols.lpCtr": "LP CTR",
  "cols.toOfferShort": "To offer",
  "cols.toOffer": "To offer",
  "cols.leads": "Leads",
  "cols.approved": "Approved",
  "cols.hold": "Hold",
  "cols.rejected": "Rejected",
  "cols.trash": "Trash",
  "cols.cr": "CR",
  "cols.outcome": "Lead outcome",
  "cols.approveRate": "Approve",
  "cols.revenue": "Revenue",
  "cols.revenueHold": "Revenue in hold",
  "cols.cost": "Cost",
  "cols.profit": "Profit",
  "cols.roi": "ROI",
  "cols.epc": "EPC",
  "cols.cpc": "CPC",
  "cols.cpl": "CPL",
  "cols.cpa": "CPA",
  "cols.roas": "ROAS",
  "cols.hint.visits": "every visit",
  "cols.hint.clicks": "people",
  "cols.hint.bots": "count",
  "cols.hint.lpCtr": "button click",
  "cols.hint.cr": "leads / clicks",
  "cols.hint.outcome": "bar",
  "cols.hint.approveRate": "of decided",
  "cols.hint.revenue": "approvals",
  "cols.hint.formula": "formula",
  "filter.add.chip": "+ Filter",
  "campaigns.quick.losing": "Losing",
  "campaigns.quick.noclicks": "No clicks",
  "campaigns.quick.hold50": "Hold > 50%",
  "campaigns.badge.active": "Active",
  "campaigns.badge.paused": "Paused",
  "campaigns.badge.draft": "Draft",
  "campaigns.badge.archived": "Archived",

  "foot.clicksWritten": "Clicks are written, lag {lag}",
  "foot.withoutClick": "{count} {postbacks} without a click",
  "word.postbacks": "postback|postbacks",
  "admin.tab.detectors": "Detectors",
  "settings.tab.history": "Change history",
  "journal.tab.postbacks": "Postbacks",
  "journal.live": "live",
  "filter.all": "all",
  "word.conversions": "conversion|conversions",
  "word.problems": "problem|problems",
  "tc.human": "Person",
  "tc.verified_bot": "Verified bot",
  "tc.meta_crawler": "Meta crawler",
  "tc.listed_ip": "Listed bot address",
  "tc.crawler": "Crawler",
  "tc.no_ua": "No User-Agent",
  "tc.datacenter": "Data centre",
  "tc.bot_score": "Bot by score",
  "tc.bot": "Bot (before classes)",
  "cj.col.time": "Time",
  "cj.col.campaign": "Campaign",
  "cj.col.status": "Status",
  "cj.col.goal": "Goal",
  "cj.col.payout": "Payout",
  "cj.col.usd": "In USD",
  "cj.col.network": "Network",
  "cj.col.txid": "Network ID",
  "cj.col.ad": "Ad",
  "cj.col.geo": "Geo",
  "cj.status.all": "All",
  "cj.afterHold": "after {days} d in hold",
  "cj.byLatestStatus": "by the latest status date",
  "cj.noCampaign": "no campaign",
  "cj.noCampaign.hint": "The tracker never saw this conversion's click, so no campaign report counts it",
  "cj.campaignGone": "Campaign unavailable",
  "cj.noRate": "no rate",
  "cj.empty": "No conversions for this period and filters.",
  "cj.error": "Could not load conversions",
  "clj.col.class": "Class",
  "clj.empty": "No clicks for this period and filters.",
  "clj.botsEmpty": "No bots in this period.",
  "clj.error": "Could not load clicks",
  "pb.all": "All",
  "pb.replayFixed": "Replay all fixed",
  "pb.replayFixed.title": "Replay {count} {postbacks}?",
  "pb.replayFixed.body": "The tracker will apply again the postbacks on this page it no longer retries by itself: the click was never found or the request was refused. If the cause is fixed, the conversion is written; if not, the result stays the same. Nothing is counted twice.",
  "pb.replayFixed.confirm": "Replay",
  "pb.replay.done": "Replay started: {count} {postbacks}",
  "pb.replay.none": "Nothing to replay — these are accepted or being applied",
  "pb.replay.failed": "Could not start the replay",
  "pb.col.received": "Received",
  "pb.col.network": "Network",
  "pb.col.click": "Click",
  "pb.col.amount": "Amount",
  "pb.col.result": "Result",
  "pb.col.became": "What happened",
  "pb.unknownNetwork": "Unknown network",
  "pb.chip.accepted": "Accepted",
  "pb.chip.duplicate": "Duplicate",
  "pb.chip.pending": "Processing",
  "pb.chip.no_click": "No click",
  "pb.chip.unknown_status": "Status not mapped",
  "pb.chip.no_fx": "No rate",
  "pb.chip.rejected": "Refused",
  "pb.chip.bad_token": "Wrong token",
  "pb.result.accepted": "Accepted",
  "pb.result.duplicate": "Duplicate, already seen",
  "pb.result.pending": "Processing",
  "pb.result.no_click": "No click",
  "pb.result.no_click.waiting": "No click, waiting {age}",
  "pb.result.unknown_status": "Status not mapped",
  "pb.result.unknown_status.word": "Status «{word}» not mapped",
  "pb.result.no_fx": "No rate",
  "pb.result.no_fx.cur": "No {cur} rate",
  "pb.result.rejected": "Refused",
  "pb.result.rejected.why": "Refused: {why}",
  "pb.result.bad_token": "Wrong token",
  "pb.became.new": "new lead, {status}",
  "pb.became.fxBackfilled": "the rate arrived — the payout is in revenue",
  "pb.became.duplicate": "the same conversion, not counted twice",
  "pb.became.rankKept": "{status} is not rolled back to hold",
  "pb.became.noClick": "the click has not arrived yet, or is not ours",
  "pb.became.noClickId": "the request carries no click",
  "pb.became.unmapped": "not written — waiting for the status to be mapped",
  "pb.became.noFx": "the payout is not in revenue",
  "pb.became.badToken": "refused, not written",
  "pb.became.rejected": "not written — the request is unreadable",
  "pb.became.pending": "queued",
  "pb.became.replaying": "replay requested, queued",
  "pb.detail.request": "Request parameters as received",
  "pb.detail.noQuery": "no parameters",
  "pb.detail.writtenLabel": "Written",
  "pb.detail.written": "conversion «{goal}», status {status}, {payout} → {usd}",
  "pb.detail.notWritten": "nothing — no conversion changed",
  "pb.detail.why": "Why",
  "pb.detail.same": "Same as this",
  "pb.detail.sameText": "{count} {postbacks} from {network} with status «{word}» in the period, {usd} USD",
  "pb.detail.no_rate": "no {cur} rate",
  "pb.detail.status_rank_kept": "a ruling is not rolled back to hold",
  "pb.detail.same_state": "the same state again",
  "pb.detail.stale_revision": "an older revision than the one written",
  "pb.detail.already_applied": "already applied",
  "pb.detail.click_not_seen_yet": "no click yet — the tracker retries",
  "pb.detail.click_missing_in_request": "the request has no click id",
  "pb.detail.foreign_click": "a click of another workspace",
  "pb.detail.click_has_no_offer_of_network": "the click has no offer of this network",
  "pb.detail.click_has_no_route_to_offer": "the click led to no offer",
  "pb.detail.status_not_mapped": "the status word is not mapped",
  "pb.detail.invalid_payout": "the payout is not a number",
  "pb.detail.invalid_currency": "unknown currency",
  "pb.detail.invalid_payload": "unreadable request",
  "pb.detail.offer_mismatch": "the request's offer does not match the click",
  "pb.detail.offer_ambiguous": "the offer is ambiguous",
  "pb.detail.missing_network": "network not found",
  "pb.detail.txid_owned_by_other_click": "this network ID belongs to another click",
  "pb.detail.invalid_partner_timestamp": "invalid partner timestamp",
  "pb.detail.invalid_partner_revision": "invalid partner revision",
  "pb.detail.unversioned_after_revision": "unversioned after a revision",
  "pb.detail.fx_backfilled": "rate backfilled",
  "pb.detail.replay_requested": "replay requested",
  "pb.todo.title": "What to do",
  "pb.todo.unmapped": "{network} sends the status «{word}». Say what it means and the tracker applies {count} {postbacks} with this word.",
  "pb.todo.mapTo": "What the status means",
  "pb.todo.map": "Map and recount {count}",
  "pb.todo.mapped": "«{word}» = {status}; applying {count}",
  "pb.todo.mapFailed": "Could not map the status",
  "pb.todo.openNetwork": "Open the network",
  "pb.todo.noClickWaiting": "The click may still be on its way — the tracker retries. If the click is not ours, the postback stays here.",
  "pb.todo.noClickDead": "The tracker stopped waiting for the click. If it has turned up or the postback link is fixed — replay.",
  "pb.todo.noFx": "The conversion is written, but the {cur} payout is not in revenue until there is a rate. It is backfilled when the rate arrives.",
  "pb.todo.badToken": "The request came with a wrong token or from an unknown address. Check the postback link at the network.",
  "pb.todo.rejected": "The request could not be read (see «Why»). Fix the network's settings and replay.",
  "pb.todo.replay": "Replay",
  "pb.empty": "No postbacks in this period.",
  "pb.error": "Could not load postbacks",
  "health.title": "Data health",
  "health.error": "Could not check data health",
  "health.p.unmapped": "{network}: status «{word}» not mapped",
  "health.p.spendNoClicks": "{sum} USD of ad spend in 7 days with no visit in the tracker",
  "health.p.spendNoClicks.sub": "{share} of all spend: its ads send people to Keitaro (campaigns not switched yet) or brought nobody that day — no campaign's ROI counts it",
  "health.p.leads": "Leads not delivered to networks: {count}",
  "health.p.leads.sub": "delivery is retried; refused ones go to the DLQ",
  "health.p.dead": "{count} events in the DLQ",
  "health.p.dead.sub": "retries exhausted — replay the stuck ones",
  "health.p.stuck": "{count} events stuck",
  "health.p.stuck.sub": "in the journal over 5 minutes, not waiting for a click",
  "health.p.lag": "Reports are {age} behind",
  "health.p.lag.sub": "the last minutes are not in the reports yet",
  "health.a.open": "Open",
  "health.ok.leads.sub": "{n} in the hour, no failures",
  "health.ok.ratesPending.sub": "the first refresh lands within an hour",
  "health.p.ratesStale": "Exchange rates not refreshed for {age}",
  "health.p.ratesNever": "Exchange rates not loaded for over 48 h",
  "health.p.ratesStale.sub": "foreign payouts and spend are valued at an old rate or left out of revenue",
  "health.p.ratesStale.error": "last error: {error}",
  "today.title.date": "Today, {date}",
  "today.updated": "updated {age} ago",
  "today.byTodaysLeads": "of today's leads",
  "today.chart.profit": "Profit today, running",
  "today.chart.leads": "Leads today, running",
  "today.chart.clicks": "Clicks today, running",
  "today.chart.series": "What the chart shows",
  "today.chart.nowLabel": "{value} now",
  "today.chart.wasLabel": "yesterday by {at}: {value}",
  "today.legend.today": "today",
  "today.legend.yesterday": "yesterday",
  "today.now": "Now, {at}",
  "today.vsYesterdaySameTime": "vs yesterday at this time",
  "today.inHold": "Still in hold",
  "today.attention": "Needs attention",
  "today.attention.calm": "All calm.",
  "today.best": "Best today",
  "today.worst": "Worst today",
  "dimNone.network": "No network",
  "dimNone.offer": "No offer",
  "dimNone.landing": "No lander",
  "dimNone.campaign": "No campaign",
  "dimNone.source": "No source",
  "dimNone.group": "No group",
  "dimNone.creative": "No ad",
  "dimNone.flow": "No flow",
  "dimWord.campaign": "campaign|campaigns",
  "dimWord.creative": "ad|ads",
  "dimWord.landing": "lander|landers",
  "dimWord.offer": "offer|offers",
  "dimWord.network": "network|networks",
  "dimWord.source": "source|sources",
  "dimWord.country": "country|countries",
  "dimWord.ad_campaign": "FB campaign|FB campaigns",
  "dimWord.ad_set": "ad set|ad sets",
  "dimWord.placement": "placement|placements",
  "dimWord.group": "group|groups",
  "dimWord.flow": "flow|flows",
  "dimWord.node": "node|nodes",
  "dimWord.step": "step|steps",
  "dimWord.branch": "branch|branches",
  "dimWord.region": "region|regions",
  "dimWord.city": "city|cities",
  "dimWord.language": "language|languages",
  "dimWord.device": "device type|device types",
  "dimWord.os": "OS|OSes",
  "dimWord.browser": "browser|browsers",
  "dimWord.device_model": "model|models",
  "dimWord.isp": "ISP|ISPs",
  "dimWord.asn": "ASN|ASNs",
  "dimWord.connection": "connection|connections",
  "dimWord.ip_subnet": "subnet|subnets",
  "dimWord.referrer": "referrer|referrers",
  "dimWord.hour": "hour|hours",
  "dimWord.weekday": "weekday|weekdays",
  "dimWord.day": "day|days",
  "dimWord.week": "week|weeks",
  "dimWord.month": "month|months",
  "dimWord.goal": "goal|goals",
  "dimWord.status": "status|statuses",
  "dimWord.traffic_class": "class|classes",
  "dimWord.bot_reason": "reason|reasons",
  "wd.1": "Mon",
  "wd.2": "Tue",
  "wd.3": "Wed",
  "wd.4": "Thu",
  "wd.5": "Fri",
  "wd.6": "Sat",
  "wd.7": "Sun",
  "status.approved": "Approved",
  "status.hold": "Hold",
  "status.rejected": "Rejected",
  "status.trash": "Trash",
  "status.refunded": "Refunded",
  "word.levels": "level|levels",
  "dimgroup.source": "Source",
  "dimgroup.path": "Path",
  "dimgroup.geo": "Geo",
  "dimgroup.device": "Device",
  "dimgroup.network": "Network",
  "dimgroup.time": "Time",
  "dimgroup.conversion": "Conversion",
  "dimgroup.quality": "Quality",
  // — карточка кампании, Setup, клик и конверсия (Т7) —
  "card.tabs": "Campaign sections",
  "card.tab.summary": "Summary",
  "card.tab.report": "Report",
  "card.tab.conversions": "Conversions",
  "card.tab.clicks": "Clicks",
  "card.tab.flow": "Flow",
  "card.tab.links": "Links & postbacks",
  "card.tab.settings": "Settings",
  "card.tab.history": "History",
  "card.meta.offer": "offer",
  "card.meta.landers": "landers {count}",
  "card.meta.buyer": "buyer {name}",
  "card.action.linkFor": "Link for {source}",
  "card.action.link": "Link",
  "card.action.edit": "Edit",
  "card.toast.copied": "Copy «{name}» made — check it before switching it on",
  "card.money.title": "Money by day",
  "card.money.revenue": "Revenue",
  "card.money.cost": "Spend",
  "card.money.profit": "Profit",
  "card.money.by": "Granularity",
  "card.money.day": "Day",
  "card.money.hour": "Hour",
  "card.money.empty": "No money in this period",
  "card.path.title": "Click path",
  "card.path.aside": "people, no bots",
  "card.path.visits": "Visits",
  "card.path.visits.note": "every hit",
  "card.path.bots": "Bots cut",
  "card.path.people": "People",
  "card.path.people.note": "= clicks",
  "card.path.shows": "Saw the lander",
  "card.path.cta": "Button click",
  "card.path.lead": "Lead",
  "card.path.approve": "Approve",
  "card.outcome.title": "Lead outcomes",
  "card.outcome.wait": "How long the hold waits",
  "card.outcome.wait.none": "Nothing in hold",
  "card.insight.title": "What stands out",
  "card.insight.aside": "for the period",
  "card.insight.none": "Nothing stands out in this period",
  "card.insight.open": "Open",
  "card.insight.compare": "Compare",
  "card.insight.report": "Report",
  "card.insight.list": "List",
  "card.insight.expensive_ad.title": "Ad {ad} is expensive",
  "card.insight.expensive_ad.body": "CPA {cpa} vs {campaignCpa} for the campaign, {cost} spent, profit {profit}",
  "card.insight.weak_lander.title": "{worst} does worse than {best}",
  "card.insight.weak_lander.body": "LP CTR {worstCtr}% vs {bestCtr}% on comparable traffic",
  "card.insight.night_rejects.title": "Night leads get refused",
  "card.insight.night_rejects.body": "{share}% of leads come 00–06, {rejected}% of them refused or trash",
  "card.insight.stale_hold.title": "{count} leads in hold over 3 days",
  "card.insight.stale_hold.body": "≈ {sum} USD wait for {network} — ask the manager",
  "card.report.spendWithoutClicks": "Spend without clicks",
  "card.clicks.lander": "Lander",
  "card.clicks.class": "Class",
  "word.people": "person|people",
  "word.bots": "bot|bots",
  "card.clicks.empty": "No visits in this period",
  "card.conv.statusAt": "Status at",
  "card.conv.ad": "Ad",
  "card.conv.wait.h": "{n} h",
  "card.settings.token": "Token",
  "card.history.system": "system",
  "card.history.empty": "No changes recorded",
  "card.history.error": "History did not load",
  "card.history.field.status": "status",
  "card.history.field.branches": "exit branches",
  "card.history.field.schedule": "schedule",
  "card.history.field.notes": "notes",
  "click.meta.at": "{at} {tz}",
  "click.notFound": "Click not found",
  "click.copied": "Copied",
  "click.markBot": "Mark as bot",
  "click.markedBot": "Marked — the scoring learns from it",
  "click.path": "Path",
  "click.path.none": "No steps recorded",
  "click.path.lander": "Lander",
  "click.path.offer": "offer",
  "click.path.lead": "Lead",
  "click.conversions.none": "No conversions on this click",
  "click.conv.open": "Open",
  "click.who": "Who",
  "click.who.isp": "Network",
  "click.who.language": "Language",
  "click.who.device": "Device",
  "click.who.noMatchKeys": "No pixel or fbclid — this click cannot be matched in Facebook",
  "conv.title": "Conversion {id}",
  "conv.meta.payout": "payout {payout}",
  "conv.meta.goal": "goal «{goal}»",
  "conv.openClick": "Open click",
  "conv.history": "How the status moved",
  "conv.history.none": "No events recorded",
  "conv.step.lead": "Lead",
  "conv.step.first": "the partner received the lead",
  "conv.step.byPostback": "by postback",
  "conv.step.after": "after {gap}",
  "conv.raw": "What the partner sent",
  "conv.raw.none": "Nothing was stored",
  "conv.raw.accepted": "Accepted {at} · {status}",
  "conv.raw.match": "matched by {source}",
  "conv.origin": "Where the lead came from",
  "conv.origin.click": "Click",
  "conv.origin.leadAfter": "lead after {gap}",
  "conv.origin.noClick": "no click found",
  "conv.origin.ad": "Ad",
  "conv.origin.campaignAd": "Ad campaign",
  "conv.origin.lander": "Lander",
  "conv.origin.geo": "Geo",
  "conv.origin.device": "Device",
  "gap.s": "{s} s",
  "gap.ms": "{m} min {s} s",
  "gap.hm": "{h} h {m} min",
  "gap.dh": "{d} d {h} h",
  "panel.close": "Close",
  "word.days": "day|days",
  "word.hours": "hour|hours",
  "word.minutes": "minute|minutes",
  "word.seconds": "second|seconds",
  "links.source": "the source",
  "links.checked": "checked: click arrived {ago} ago",
  "links.waiting": "waiting for the test click",
  "links.adField": "goes into «{field}»",
  "links.domainNotServing": "{fqdn} does not serve yet — wait for the binding before launching",
  "links.partners": "For partners",
  "links.test.send": "Send test postback",
  "links.test.needClick": "Open a test click first — the test goes only to your own click",
  "links.test.sent": "Test postback: {result}",
  "links.unmapped": "Status «{status}» from {network} is not mapped — such leads go to Health, not to hold. Say what it means →",
  "links.recent": "Latest postbacks",
  "links.recent.none": "No postbacks in this period",
  "links.recent.when": "When",
  "links.recent.from": "From",
  "links.recent.got": "Got",
  "links.recent.did": "What we did",
  "flow.edit": "Change rules",
  "flow.rules.none": "No rules — everybody goes by shares",
  "flow.branch.back": "Back button",
  "flow.branch.exit": "Exit intent",
  "flow.branch.idle": "Idle",
  "flow.branch.scroll": "Scrolled to the end",
  "setup.crumbNew": "New",
  "setup.name": "Name",
  "setup.copy": "Copy",
  "setup.copied": "Copied",
  "setup.source.title": "Source",
  "setup.source.platform": "Platform",
  "setup.source.pixel": "Pixel",
  "setup.group": "Group",
  "setup.group.none": "No group",
  "setup.tags": "Tags",
  "setup.notes": "Notes",
  "setup.offers.offer": "Offer",
  "setup.offers.landers": "Landers",
  "setup.rules.title": "Flow rules",
  "setup.rules.if": "If",
  "setup.rules.match.pick": "Pick a condition",
  "setup.rules.match.country": "country is",
  "setup.rules.match.notCountry": "country is not",
  "setup.rules.match.device": "device",
  "setup.rules.match.bot": "bot or crawler",
  "setup.rules.match.schedule": "hours",
  "setup.rules.match.paramNot": "link lacks the key",
  "setup.rules.match.field": "field of the visit",
  "setup.rules.device.mobile": "mobile",
  "setup.rules.device.tablet": "tablet",
  "setup.rules.device.desktop": "desktop",
  "setup.rules.target.offer": "offer {name}",
  "setup.rules.target.lander": "lander {name}",
  "setup.domain.title": "Domain",
  "setup.domain.domain": "Your own free domain",
  "setup.domain.https": "HTTPS ready",
  "setup.domain.binding": "still binding",
  "setup.domain.path": "Path",
  "setup.cost.title": "Spend",
  "setup.cost.auto": "From Facebook",
  "setup.cost.cpc": "Per click",
  "setup.cost.cpa": "Per lead",
  "setup.cost.revshare": "Revenue share",
  "setup.cost.manual": "Manual by period",
  "setup.cost.rate": "Rate, USD",
  "setup.postbacks.none": "Pick an offer — its partner's postback appears here",
  "setup.postbacks.last": "last {ago} ago",
  "setup.postbacks.never": "no postbacks for a week",
  "links.capi.idle": "no events",
  "links.capi.live": "sending",
  "links.capi.test": "test only",
  "links.capi.none": "No events for this campaign yet — no partner ruling on its leads so far.",
  "links.capi.state.delivered": "handed on for sending",
  "links.capi.state.waiting": "waiting",
  "links.capi.state.dead": "failed",
  "links.capi.state.test": "test, not sent to Facebook",
  // — каталоги больше страницы: выбор, имена, списки —
  "picker.offer.pick": "Pick an offer",
  "picker.offer.search": "Search all offers…",
  "picker.offer.none": "No offer",
  "picker.lander.pick": "Pick a lander",
  "picker.lander.search": "Search all landers…",
  "picker.lander.none": "No lander",
  "picker.flow.pick": "Pick a flow",
  "picker.flow.search": "Search all flows…",
  "picker.flow.none": "No flow",
  "picker.source.pick": "Pick a traffic source",
  "picker.source.search": "Search sources…",
  "picker.source.none": "No source",
  "picker.domain.pick": "Pick a domain",
  "picker.domain.search": "Search all domains…",
  "picker.domain.none": "No domain",
  "picker.campaign.pick": "Pick a campaign",
  "picker.campaign.search": "Search all campaigns…",
  "picker.campaign.none": "All campaigns",
  "picker.keys.move": "move",
  "picker.keys.pick": "pick",
  "picker.failed": "Could not load the list",
  "picker.nothing": "Nothing found",
  "picker.more": "{count} more — refine the search",
  "links.failed": "Could not render the link",
  "links.noSource": "No traffic source is chosen for this campaign, so there is no ad link to show.",
  "links.noSource.pick": "Choose the source in Settings",
  "links.preview": "Shown as a check, not a link to paste: {reason}",
  // — выбор ленда в редакторе —
  "picker.usedIn": "in {flows} flows · {campaigns} campaigns",
} as const;

/** Every key the app may ask for. `ru` must cover all of them. */
export type TKey = keyof typeof en;
